import { useCallback, useEffect, useRef, useState } from 'react'
import { followSpanishQuestion } from '@/features/companion/language-follow'
import { useLocation } from 'react-router-dom'
import { useCompanionManager } from '@edge/edge-ai'
import type { Message, CompanionEvent, Pending } from '@edge/edge-ai'
import { useChatStore } from '@/stores/chat-store'
import { getThread, saveThread, type StoredThread } from '@/stores/conversation-idb'
import { useThreadStore } from '@/stores/thread-store'
import { useEditDiffStore } from '@/features/companion/edit-diff-store'
import { schedulePush, fetchRemoteThread } from '@/features/companion/thread-sync'
import { clampChoices } from '@/types/schemas/companion-threads'
import { getPanelLiveState, formatPanelStateForPrompt } from '@/stores/panel-state-registry'
import { getContextKeyFromPath } from './chat-context-key'
import { emitChecklistSignal } from '@/features/onboarding/checklist-signals'
import { useUsageRefresh } from '@/features/deep-research/hooks/use-usage-refresh'
import { watchTurnEnds } from './watch-turn-ends'
import { captureMessagePanels, clearTurnPanels } from '@/features/companion/turn-panels'
import { useDocGapStore } from '@/features/companion/doc-gap-store'

/**
 * The companion conversation engine, extracted from ChatDrawer so every shell
 * (side drawer, floating window) renders the SAME conversation without
 * duplicating the sync/persistence logic.
 *
 * MUST BE MOUNTED EXACTLY ONCE (see CompanionHost). It owns:
 *  - mirroring CompanionManager's message stream and its pending slot into React state
 *  - the live-typing bubble (text-delta accumulation)
 *  - rehydrating the ACTIVE THREAD from IDB on load (threads follow you
 *    across routes — navigation no longer swaps the conversation)
 *  - saving the thread to IDB on every message change (lazy-minting the
 *    thread id on the first send)
 *  - page-context propagation to the manager
 *  - the mention preamble + send / Do it / new-thread / select-thread handlers
 *
 * Mounting it twice would double the manager subscription and race the IDB
 * rehydrate — that is why shells receive its return value as props instead of
 * calling it themselves.
 */
export function useCompanionConversation() {
  const manager = useCompanionManager()
  const clearDiffCards = useEditDiffStore((s) => s.clear)
  const { pathname, search } = useLocation()
  const [messages, setMessages] = useState<Message[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [toolCount, setToolCount] = useState(0)
  // Live "typing" text accumulated from text-delta events, shown as a
  // provisional bubble until the final `message` event replaces it.
  const [streamingText, setStreamingText] = useState('')
  // What the thread waits on from the trader (the manager's pending slot,
  // spec 2026-10-18, 6.2): a walkthrough behind its Start button, say.
  const [pending, setPending] = useState<Pending | null>(null)

  // `search` matters: the backtest editor resolves its strategy from
  // ?strategy= when the path has no id. The context key is thread METADATA
  // now (which page a thread started on) — not the storage key.
  const { key: contextKey, page } = getContextKeyFromPath(pathname, search)
  const activeThreadId = useThreadStore((s) => s.activeThreadId)
  // Metadata stamped on the thread at creation (or restored with it): the
  // page/context it STARTED on, immune to later navigation.
  const threadMetaRef = useRef<{
    page: string
    originContextKey: string
    createdAt: number
  } | null>(null)

  // Tell the companion which page it's on, from the route. Scopes are open
  // strings now (registry-backed) — pass the route-derived page straight
  // through. Unregistered scopes (e.g. 'research', 'general') resolve to
  // global tools only, which also stops the DOM heuristic from misclassifying
  // non-workspace pages as 'dashboard'. The host is mounted globally in
  // app-layout, so this covers voice too (same manager instance).
  useEffect(() => {
    manager.setPageContext(page)
  }, [manager, page])

  // The thread id the rehydrate below last ran for.
  const rehydratedIdRef = useRef<string | null>(null)

  // The thread exists from the first send, so the first request already names
  // its conversation for analytics (session_id → PostHog $ai_session_id). Marked
  // rehydrated: it is live, there is nothing saved to load for it yet.
  const ensureThread = useCallback(() => {
    const threads = useThreadStore.getState()
    let id = threads.activeThreadId
    if (id === null) {
      id = crypto.randomUUID()
      rehydratedIdRef.current = id
      threads.setActiveThread(id)
    }
    manager.setSessionId(id)
  }, [manager])

  // ── Sync messages from CompanionManager ──────────────────

  useEffect(() => {
    setMessages([...manager.messages])
    setPending(manager.pending)

    const handler = (event: CompanionEvent) => {
      if (event.type === 'message') {
        // Every send (this chat, voice, the inline composer, the backtest
        // panels, the pop-out window) posts its user message here before the
        // manager makes any request, so the first request names the thread.
        if (event.message.role === 'user') ensureThread()
        setMessages([...manager.messages])
        setToolCount(0)
        setStreamingText('') // final message replaces the live bubble
      }
      // The thread's pending slot changed (spec 2026-10-18, 6.2): a new
      // question took it, or a tool parked a walkthrough. When an answer
      // forgot a withdrawn offer (Start walkthrough, Dismiss), the thread is
      // saved again so a reopened one does not offer it twice; otherwise
      // nothing changed that a save would keep.
      if (event.type === 'pending') {
        setPending(event.pending)
        setMessages((prev) =>
          prev.length === manager.messages.length && prev.every((m, i) => m === manager.messages[i])
            ? prev
            : [...manager.messages],
        )
      }
      if (event.type === 'text-delta') {
        setStreamingText((prev) => prev + event.text)
      }
      if (event.type === 'text-reset') setStreamingText('')
      if (event.type === 'state-change') {
        setIsProcessing(event.state === 'processing')
        if (event.state === 'processing') setStreamingText('') // new turn
        if (event.state !== 'processing') setToolCount(0)
      }
      if (event.type === 'tool-progress') {
        setToolCount(event.count)
      }
      if (event.type === 'error') {
        // The error surfaces as an assistant message (pushed by the manager on
        // both the chat AND voice paths); here we just clear the transient
        // spinner / live-typing bubble so nothing dangles after a failure.
        setStreamingText('')
        setIsProcessing(false)
        setToolCount(0)
      }
    }

    return manager.events.on(handler)
  }, [manager, ensureThread])

  // Credits are reserved server-side on every turn; re-read them when it ends.
  const refreshUsage = useUsageRefresh()
  useEffect(() => watchTurnEnds(manager.events, refreshUsage), [manager, refreshUsage])

  // ── Rehydrate the active thread on load ──────────────────────────────────
  // The manager holds messages in memory only, so a full page reload would
  // show an empty conversation even though the thread is persisted in IDB.
  // Restore it — INTO THE MANAGER TOO (loadMessages), so the model remembers
  // the turns the user can see. Guards: never clobber an in-memory
  // conversation or a turn that starts while the async load is in flight; run
  // at most once per thread id.
  useEffect(() => {
    if (activeThreadId === null || rehydratedIdRef.current === activeThreadId) return
    rehydratedIdRef.current = activeThreadId
    if (manager.messages.length > 0) return
    let cancelled = false
    getThread(activeThreadId).then((stored) => {
      if (cancelled || !stored || stored.messages.length === 0) return
      if (manager.messages.length > 0) return // a turn started meanwhile
      threadMetaRef.current = {
        page: stored.page,
        originContextKey: stored.originContextKey,
        createdAt: stored.createdAt,
      }
      manager.loadMessages(stored.messages as unknown as Message[])
      // The thread's own slot came with it (a tour its last answer parked);
      // loading emits no event.
      setPending(manager.pending)
      setMessages(stored.messages as unknown as Message[])
    })
    return () => {
      cancelled = true
    }
  }, [activeThreadId, manager])

  // ── Save the thread to IDB on message changes ────────────
  // Lazy-minting: the first message of a fresh chat creates the thread id and
  // stamps the origin metadata from the CURRENT route.

  useEffect(() => {
    if (messages.length === 0) return

    const threadStore = useThreadStore.getState()
    let id = threadStore.activeThreadId
    if (id === null) {
      id = crypto.randomUUID()
      rehydratedIdRef.current = id // already live — nothing to rehydrate
      threadStore.setActiveThread(id)
    }
    if (threadMetaRef.current === null) {
      threadMetaRef.current = {
        page,
        originContextKey: contextKey,
        createdAt: messages[0]?.timestamp ?? Date.now(),
      }
    }
    const meta = threadMetaRef.current

    const thread: StoredThread = {
      id,
      title: messages[0]?.text.slice(0, 60) || 'Untitled',
      messages: messages.map((m) => ({
        role: m.role,
        text: m.text,
        timestamp: m.timestamp,
        toolCalls: m.toolCalls,
        toolResults: m.toolResults,
        choices: clampChoices(m.choices),
        pending: m.pending,
      })),
      page: meta.page,
      originContextKey: meta.originContextKey,
      createdAt: meta.createdAt,
      updatedAt: Date.now(),
      dirty: true,
    }
    void saveThread(thread).then(() => schedulePush())
  }, [messages, page, contextKey])

  // ── Route/context changes ────────────────────────────────
  // The thread follows the user across pages now; only the edit-diff cards
  // reset (their Undo snapshots belong to the graph on the page you left).

  useEffect(() => {
    clearDiffCards()
  }, [contextKey, clearDiffCards])

  // ── Send message ─────────────────────────────────────────

  // Panel mentions (from `@`-click or a panel's "Ask AI" menu item) are
  // prepended to the outgoing text as a preamble the AI can key off, then
  // cleared — they're a one-shot annotation on the next message, not
  // persistent context.
  const withMentionPreamble = useCallback((text: string) => {
    const mentions = useChatStore.getState().mentions
    // The mentioned panels' DATA rides separately, as structured evidence for
    // every analyst call (turn-panels.ts). New mentions replace the attached
    // panels, and so does a panel the message names ("my scanner"); a plain
    // follow-up keeps them.
    captureMessagePanels(text, mentions)
    // A new question starts with no missing-guide offer (doc-gap-store.ts).
    useDocGapStore.getState().clear()
    if (mentions.length === 0) return text
    // Inject each mentioned panel's LIVE on-screen state so the AI knows what's
    // actually in it (its ticker, filters, columns, listed tickers…), not just
    // its type/id. Falls back to the header alone for panels that don't register.
    const parts = mentions.map((m) => {
      const header = `"${m.title}" (type: ${m.componentType}, id: ${m.panelId})`
      const state = getPanelLiveState(m.panelId)
      const snapshot = state ? formatPanelStateForPrompt(state) : ''
      return snapshot ? `${header}\n${snapshot}` : header
    })
    const preamble = `[The user is referring to these panel(s), with their current on-screen state:\n${parts.join('\n\n')}]\n\n`
    useChatStore.getState().clearMentions()
    return preamble + text
  }, [])

  // Switching threads, or New chat (null until the next send), renames it.
  useEffect(() => {
    manager.setSessionId(activeThreadId)
  }, [activeThreadId, manager])

  const handleSend = useCallback(
    (text: string) => {
      // The option follows a Spanish question BEFORE the send, on this turn.
      followSpanishQuestion(text, manager)
      manager.sendChatMessage(withMentionPreamble(text))
      // Onboarding checklist "ask the companion" signal; no-op without an
      // active checklist.
      emitChecklistSignal('companion-ask')
    },
    [manager, withMentionPreamble],
  )

  // Smart-nudge "Do it": re-send the original request as a turn that may
  // change the workspace (2026-10-15, Phase 2b). The recovery when the turn
  // check missed a change request; the user asked explicitly, so there is no
  // second check, and no persona switch.
  const handleDoIt = useCallback(
    (text: string) => {
      manager.sendChatMessage(withMentionPreamble(text), { act: true })
    },
    [manager, withMentionPreamble],
  )

  // ── New conversation ─────────────────────────────────────

  const handleNewConversation = useCallback(() => {
    // Clear the MANAGER too — leaving its history in place resurrected the
    // "cleared" conversation on the next message event and kept leaking old
    // turns into the LLM context (pre-thread latent bug).
    manager.clearHistory()
    // @-mentioned panels belong to the conversation they were mentioned in,
    // and so does a walkthrough offered under one of its replies.
    clearTurnPanels()
    setPending(null)
    setMessages([])
    clearDiffCards()
    threadMetaRef.current = null
    useThreadStore.getState().startNewThread()
  }, [manager, clearDiffCards])

  // ── History navigation ───────────────────────────────────

  const handleSelectConversation = useCallback(
    async (id: string) => {
      // Never swap history under a running tool loop.
      if (manager.state === 'processing') return
      clearTurnPanels()
      // Local cache first; a remote-only thread (from the pull-merged list)
      // is fetched and cached on demand.
      const stored = (await getThread(id)) ?? (await fetchRemoteThread(id))
      if (!stored) return
      rehydratedIdRef.current = id
      threadMetaRef.current = {
        page: stored.page,
        originContextKey: stored.originContextKey,
        createdAt: stored.createdAt,
      }
      // The slot moves with the thread: the one left keeps its own in its
      // saved answer, and a tour the new one parked comes back. Loading emits
      // no event.
      manager.loadMessages(stored.messages as unknown as Message[])
      setPending(manager.pending)
      setMessages(stored.messages as unknown as Message[])
      clearDiffCards()
      useThreadStore.getState().setActiveThread(id)
    },
    [manager, clearDiffCards],
  )

  return {
    manager,
    messages,
    pending,
    isProcessing,
    toolCount,
    streamingText,
    contextKey,
    page,
    handleSend,
    handleDoIt,
    handleNewConversation,
    handleSelectConversation,
  }
}

export type CompanionConversation = ReturnType<typeof useCompanionConversation>
